import React, { useEffect, useState } from 'react';
import { AppShell } from '../components/layout/AppShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Dialog as Modal } from '@/components/ui/dialog';
import { Card, CardHeader, CardContent as CardBody } from '@/components/ui/card';
import {
  apiClient,
  type CreatedMcpAccess,
  type ManagementApiKey,
  type McpAccessToken,
  type NotificationChannel,
  type NotificationEventType,
  type OrganizationConfig,
} from '../lib/api-client';
import { useToast } from '../hooks/useToast';
import { useAuthStore } from '../store/authStore';

export const SettingsPage: React.FC = () => {
  const [configSnapshot, setConfigSnapshot] = useState<OrganizationConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showApiKeyModal, setShowApiKeyModal] = useState(false);
  const [showRevokeKeyModal, setShowRevokeKeyModal] = useState(false);
  const [apiKeys, setApiKeys] = useState<ManagementApiKey[]>([]);
  const [revokeKey, setRevokeKey] = useState<ManagementApiKey | null>(null);
  const [newApiKey, setNewApiKey] = useState<string | null>(null);
  const [apiKeyName, setApiKeyName] = useState('');
  const [mcpTokens, setMcpTokens] = useState<McpAccessToken[]>([]);
  const [showMcpModal, setShowMcpModal] = useState(false);
  const [mcpName, setMcpName] = useState('Agent access');
  const [mcpPreset, setMcpPreset] = useState<'observe' | 'publish' | 'manage'>('observe');
  const [mcpExpiry, setMcpExpiry] = useState('90');
  const [creatingMcp, setCreatingMcp] = useState(false);
  const [createdMcp, setCreatedMcp] = useState<CreatedMcpAccess | null>(null);
  const [topicBellChannel, setTopicBellChannel] = useState<NotificationChannel | null>(null);
  const [topicBellAvailable, setTopicBellAvailable] = useState(false);
  const [topicBellApiBase, setTopicBellApiBase] = useState('https://api.topicbell.com');
  const [topicBellDisplayName, setTopicBellDisplayName] = useState('My TopicBell channel');
  const [topicBellTopic, setTopicBellTopic] = useState('');
  const [topicBellToken, setTopicBellToken] = useState('');
  const [topicBellEvents, setTopicBellEvents] = useState<NotificationEventType[]>([
    'transmission_error_summary',
    'sensitive_data_alert',
    'security_alert',
    'pattern_analysis_complete',
  ]);
  const [topicBellEnabled, setTopicBellEnabled] = useState(true);
  const [savingTopicBell, setSavingTopicBell] = useState(false);
  const [testingTopicBell, setTestingTopicBell] = useState(false);
  const [removingTopicBell, setRemovingTopicBell] = useState(false);

  const [avoidanceCostPerGib, setAvoidanceCostPerGib] = useState<number | ''>('');

  const { success, error: showError } = useToast();
  const user = useAuthStore((state) => state.user);
  const canManageMcp = user?.role === 'owner' || user?.role === 'admin';

  useEffect(() => {
    loadConfig();
    loadApiKeys();
    loadNotificationChannels();
    if (canManageMcp) loadMcpTokens();
  }, []);

  useEffect(() => {
    if (!loading && canManageMcp && window.location.hash === '#hosted-mcp') {
      requestAnimationFrame(() => {
        document.getElementById('hosted-mcp')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }, [loading, canManageMcp]);

  const loadApiKeys = async () => {
    try {
      const response = await apiClient.getApiKeys();
      setApiKeys(response.api_keys || []);
    } catch (err: any) {
      showError(err.message || 'Failed to load management API credentials');
    }
  };

  const loadMcpTokens = async () => {
    try {
      const response = await apiClient.getMcpAccessTokens();
      setMcpTokens(response.tokens);
    } catch (err: any) {
      showError(err.message || 'Failed to load MCP access');
    }
  };

  const loadNotificationChannels = async () => {
    try {
      const response = await apiClient.getNotificationChannels();
      const topicBell = response.channels.find((channel) => channel.channel_type === 'topicbell') || null;
      setTopicBellAvailable(response.providers.topicbell.available);
      setTopicBellApiBase(response.providers.topicbell.api_base_url);
      setTopicBellChannel(topicBell);
      if (topicBell) {
        setTopicBellDisplayName(topicBell.display_name);
        setTopicBellTopic(topicBell.topic);
        setTopicBellEvents(topicBell.event_types);
        setTopicBellEnabled(topicBell.enabled);
      }
    } catch (err: any) {
      showError(err.message || 'Failed to load notification channels');
    }
  };

  const loadConfig = async () => {
    setLoading(true);
    try {
      const data = await apiClient.getConfig();
      setConfigSnapshot(data);
      setAvoidanceCostPerGib(data.avoidance_cost_per_gib_usd ?? '');
    } catch (err: any) {
      showError(err.message || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!configSnapshot) return;
    setSaving(true);
    try {
      await apiClient.updateConfig({
        retention_days: configSnapshot.retention_days,
        alert_email: configSnapshot.alert_email,
        alert_thresholds: configSnapshot.alert_thresholds,
        data_regions: configSnapshot.data_regions || ['auto'],
        cost_limit_usd_per_month: configSnapshot.cost_limit_usd_per_month,
        avoidance_cost_per_gib_usd: avoidanceCostPerGib === ''
          ? null
          : avoidanceCostPerGib,
      });
      success('Planning rate saved');
      loadConfig();
    } catch (err: any) {
      showError(err.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleRevokeApiKey = async () => {
    if (!revokeKey) return;
    try {
      await apiClient.revokeApiKey(revokeKey.id);
      success('Management API credential revoked');
      setShowRevokeKeyModal(false);
      setRevokeKey(null);
      await loadApiKeys();
    } catch (err: any) {
      showError(err.message || 'Failed to revoke API key');
    }
  };

  const handleCreateApiKey = async () => {
    try {
      const response: any = await apiClient.rotateApiKey(apiKeyName || undefined);
      setNewApiKey(response.api_key);
      success('Management API credential created');
      await loadApiKeys();
    } catch (err: any) {
      showError(err.message || 'Failed to create API key');
    }
  };

  const handleCreateMcp = async () => {
    setCreatingMcp(true);
    try {
      const response = await apiClient.createMcpAccessToken({
        name: mcpName,
        preset: mcpPreset,
        expires_in_days: mcpExpiry === 'never' ? null : Number(mcpExpiry),
      });
      setCreatedMcp(response);
      success('MCP access created');
      await loadMcpTokens();
    } catch (err: any) {
      showError(err.message || 'Failed to create MCP access');
    } finally {
      setCreatingMcp(false);
    }
  };

  const toggleTopicBellEvent = (eventType: NotificationEventType) => {
    setTopicBellEvents((current) => current.includes(eventType)
      ? current.filter((value) => value !== eventType)
      : [...current, eventType]);
  };

  const handleSaveTopicBell = async () => {
    if (!topicBellTopic.trim() || (!topicBellChannel && !topicBellToken.trim()) || topicBellEvents.length === 0) return;
    setSavingTopicBell(true);
    try {
      const saved = await apiClient.saveTopicBellChannel({
        display_name: topicBellDisplayName.trim() || 'My TopicBell channel',
        topic: topicBellTopic.trim(),
        ...(topicBellToken.trim() ? { token: topicBellToken.trim() } : {}),
        event_types: topicBellEvents,
        enabled: topicBellEnabled,
      });
      setTopicBellChannel(saved.channel);
      setTopicBellToken('');
      try {
        await apiClient.testTopicBellChannel();
        success('TopicBell connected and test notification sent');
      } catch (testError: any) {
        showError(`Connection saved, but verification failed: ${testError.message || 'Unknown error'}`);
      }
      await loadNotificationChannels();
    } catch (err: any) {
      showError(err.message || 'Failed to connect TopicBell');
    } finally {
      setSavingTopicBell(false);
    }
  };

  const handleTestTopicBell = async () => {
    setTestingTopicBell(true);
    try {
      await apiClient.testTopicBellChannel();
      success('TopicBell test notification sent');
      await loadNotificationChannels();
    } catch (err: any) {
      showError(err.message || 'TopicBell verification failed');
      await loadNotificationChannels();
    } finally {
      setTestingTopicBell(false);
    }
  };

  const handleRemoveTopicBell = async () => {
    if (!window.confirm('Disconnect TopicBell? LogLimit will stop sending notifications to this channel.')) return;
    setRemovingTopicBell(true);
    try {
      await apiClient.deleteTopicBellChannel();
      setTopicBellChannel(null);
      setTopicBellTopic('');
      setTopicBellToken('');
      setTopicBellDisplayName('My TopicBell channel');
      setTopicBellEvents(['transmission_error_summary', 'sensitive_data_alert', 'pattern_analysis_complete']);
      setTopicBellEnabled(true);
      success('TopicBell disconnected');
    } catch (err: any) {
      showError(err.message || 'Failed to disconnect TopicBell');
    } finally {
      setRemovingTopicBell(false);
    }
  };

  const handleRevokeMcp = async (token: McpAccessToken) => {
    if (!window.confirm(`Revoke MCP access “${token.name}”? Agents using it will stop immediately.`)) return;
    try {
      await apiClient.revokeMcpAccessToken(token.id);
      success('MCP access revoked');
      await loadMcpTokens();
    } catch (err: any) {
      showError(err.message || 'Failed to revoke MCP access');
    }
  };

  const copyText = async (value: string, label: string) => {
    await navigator.clipboard.writeText(value);
    success(`${label} copied to clipboard`);
  };

  if (loading) {
    return (
      <AppShell>
        <div className="space-y-6 animate-pulse">
          <div className="h-8 bg-muted rounded w-1/4"></div>
          <div className="h-64 bg-muted rounded"></div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold">Settings</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Manage v1 usage planning and programmatic access.
          </p>
        </div>

        <Card>
          <CardHeader>
            <h3 className="text-lg font-semibold">Transmission savings estimate</h3>
          </CardHeader>
          <CardBody>
            <div className="max-w-md space-y-2">
              <Input
                label="Avoided cloud cost (USD/GiB)"
                type="number"
                min="0"
                step="0.01"
                value={avoidanceCostPerGib}
                onChange={(event) => setAvoidanceCostPerGib(event.target.value === '' ? '' : Number(event.target.value))}
                placeholder="Optional"
              />
              <p className="text-xs leading-5 text-muted-foreground">
                Optional organization-supplied planning rate used to estimate avoided transmission and storage cost. It is not LogLimit pricing or an invoice.
              </p>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save planning rate'}
              </Button>
            </div>
          </CardBody>
        </Card>

        <Card id="notification-channels" className="scroll-mt-24">
          <CardHeader>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h3 className="text-lg font-semibold">Notification channels</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  Connect your own TopicBell topic for this LogLimit organization.
                </p>
              </div>
              {topicBellChannel && (
                <span className={`w-fit rounded-full px-2.5 py-1 text-xs font-medium ${
                  topicBellChannel.last_error
                    ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                    : topicBellChannel.last_verified_at
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                      : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                }`}>
                  {topicBellChannel.last_error ? 'Needs attention' : topicBellChannel.last_verified_at ? 'Verified' : 'Saved'}
                </span>
              )}
            </div>
          </CardHeader>
          <CardBody>
            <div className="max-w-2xl space-y-5">
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm leading-6 text-blue-900 dark:border-blue-800/50 dark:bg-blue-900/20 dark:text-blue-200">
                Create a private topic and a <strong>publish-only topic token</strong> in{' '}
                <a className="underline underline-offset-2" href="https://dash.topicbell.com/apps" target="_blank" rel="noreferrer">
                  TopicBell
                </a>, then paste them below. LogLimit encrypts the token before storing it and never returns it to the browser.
              </div>

              {!topicBellAvailable && (
                <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 text-sm text-yellow-900 dark:border-yellow-800/50 dark:bg-yellow-900/20 dark:text-yellow-200">
                  TopicBell connections are unavailable until this LogLimit deployment configures its notification credential encryption key.
                </div>
              )}

              <div className="grid gap-4 sm:grid-cols-2">
                <Input
                  label="Channel name"
                  value={topicBellDisplayName}
                  maxLength={80}
                  onChange={(event) => setTopicBellDisplayName(event.target.value)}
                  placeholder="My TopicBell channel"
                  disabled={!topicBellAvailable}
                />
                <Input
                  label="Topic ID"
                  value={topicBellTopic}
                  maxLength={128}
                  onChange={(event) => setTopicBellTopic(event.target.value)}
                  placeholder="my-team-alerts"
                  disabled={!topicBellAvailable}
                />
              </div>

              <div>
                <Input
                  label={topicBellChannel ? 'Replace topic token (optional)' : 'Publish-only topic token'}
                  type="password"
                  value={topicBellToken}
                  autoComplete="new-password"
                  onChange={(event) => setTopicBellToken(event.target.value)}
                  placeholder={topicBellChannel ? `${topicBellChannel.credential_prefix}… (stored encrypted)` : 'tb_pat_…'}
                  disabled={!topicBellAvailable}
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  Provider: <code>{topicBellApiBase}</code>. The token must be scoped only to the selected topic with publish access.
                </p>
              </div>

              <fieldset className="space-y-2">
                <legend className="text-sm font-medium">Send these events</legend>
                <label className="flex items-start gap-3 rounded-lg border p-3 text-sm">
                  <input
                    className="mt-0.5 h-4 w-4"
                    type="checkbox"
                    checked={topicBellEvents.includes('transmission_error_summary')}
                    onChange={() => toggleTopicBellEvent('transmission_error_summary')}
                    disabled={!topicBellAvailable}
                  />
                  <span><strong>Error and fatal transmissions</strong><span className="mt-0.5 block text-xs text-muted-foreground">One bounded summary per accepted v1 batch; no log messages or attributes leave LogLimit.</span></span>
                </label>
                <label className="flex items-start gap-3 rounded-lg border p-3 text-sm">
                  <input
                    className="mt-0.5 h-4 w-4"
                    type="checkbox"
                    checked={topicBellEvents.includes('sensitive_data_alert')}
                    onChange={() => toggleTopicBellEvent('sensitive_data_alert')}
                    disabled={!topicBellAvailable}
                  />
                  <span><strong>Sensitive-data alerts</strong><span className="mt-0.5 block text-xs text-muted-foreground">Bounded counts and detection category only; no raw log body, attributes, or source details.</span></span>
                </label>
                <label className="flex items-start gap-3 rounded-lg border p-3 text-sm">
                  <input
                    className="mt-0.5 h-4 w-4"
                    type="checkbox"
                    checked={topicBellEvents.includes('security_alert')}
                    onChange={() => toggleTopicBellEvent('security_alert')}
                    disabled={!topicBellAvailable}
                  />
                  <span><strong>Linux security alerts</strong><span className="mt-0.5 block text-xs text-muted-foreground">Severity, bounded evidence summary, and an incident link; raw authentication messages are never forwarded.</span></span>
                </label>
                <label className="flex items-start gap-3 rounded-lg border p-3 text-sm">
                  <input
                    className="mt-0.5 h-4 w-4"
                    type="checkbox"
                    checked={topicBellEvents.includes('pattern_analysis_complete')}
                    onChange={() => toggleTopicBellEvent('pattern_analysis_complete')}
                    disabled={!topicBellAvailable}
                  />
                  <span><strong>Pattern-analysis completion</strong><span className="mt-0.5 block text-xs text-muted-foreground">Job ID, service, and pattern count when an analysis finishes.</span></span>
                </label>
              </fieldset>

              <label className="flex items-center gap-3 text-sm">
                <input
                  className="h-4 w-4"
                  type="checkbox"
                  checked={topicBellEnabled}
                  onChange={(event) => setTopicBellEnabled(event.target.checked)}
                  disabled={!topicBellAvailable}
                />
                Enable delivery to this channel
              </label>

              {topicBellChannel && (
                <div className="rounded-lg bg-muted p-3 text-xs leading-5 text-muted-foreground">
                  Last verified: {topicBellChannel.last_verified_at ? new Date(topicBellChannel.last_verified_at * 1000).toLocaleString() : 'never'}
                  {' · '}Last delivery: {topicBellChannel.last_delivery_at ? new Date(topicBellChannel.last_delivery_at * 1000).toLocaleString() : 'never'}
                  {topicBellChannel.last_error && <span className="block text-red-600 dark:text-red-400">{topicBellChannel.last_error}</span>}
                </div>
              )}

              <div className="flex flex-col gap-2 sm:flex-row">
                <Button
                  onClick={handleSaveTopicBell}
                  disabled={
                    !topicBellAvailable ||
                    savingTopicBell ||
                    !topicBellTopic.trim() ||
                    (!topicBellChannel && !topicBellToken.trim()) ||
                    topicBellEvents.length === 0
                  }
                >
                  {savingTopicBell ? 'Saving…' : 'Save and verify'}
                </Button>
                {topicBellChannel && (
                  <>
                    <Button variant="outline" onClick={handleTestTopicBell} disabled={testingTopicBell || !topicBellAvailable}>
                      {testingTopicBell ? 'Sending…' : 'Send test'}
                    </Button>
                    <Button variant="destructive" onClick={handleRemoveTopicBell} disabled={removingTopicBell}>
                      {removingTopicBell ? 'Disconnecting…' : 'Disconnect'}
                    </Button>
                  </>
                )}
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Management API credentials */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Management API credentials</h3>
            </div>
          </CardHeader>
          <CardBody>
            <p className="text-sm text-muted-foreground">
              These credentials grant programmatic access to organization APIs. They are not the
              project connection keys used by the Node SDK; those live on Projects.
            </p>

            <div className="mt-4 space-y-4">
              <div className="p-4 bg-yellow-50 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300 border border-yellow-200 dark:border-yellow-800/50 rounded-lg">
                <h4 className="font-medium mb-1">Security Note</h4>
                <p className="text-sm">
                  Management API credentials have full access to your organization. Rotate them regularly.
                  If a key is compromised, revoke it immediately.
                </p>
              </div>

              <div className="space-y-3">
                {apiKeys.length === 0 ? <p className="text-sm text-muted-foreground">No management API credentials. Browser sessions and project connection keys continue independently.</p> : apiKeys.map((key) => (
                  <div key={key.id} className="flex flex-col gap-3 rounded-lg border p-3 sm:flex-row sm:items-center sm:justify-between">
                    <div><p className="text-sm font-medium">{key.name}</p><p className="mt-1 text-xs text-muted-foreground">Created {new Date(key.created_at * 1000).toLocaleDateString()} · Last used {key.last_used_at ? new Date(key.last_used_at * 1000).toLocaleString() : 'never'}{key.revoked_at ? ' · Revoked' : ''}</p></div>
                    {!key.revoked_at && <Button variant="destructive" size="sm" onClick={() => { setRevokeKey(key); setShowRevokeKeyModal(true); }}>Revoke</Button>}
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setApiKeyName('');
                    setNewApiKey(null);
                    setShowApiKeyModal(true);
                  }}
                >
                  Create management credential
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>

        {canManageMcp && (
          <Card id="hosted-mcp" className="scroll-mt-24">
            <CardHeader>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h3 className="text-lg font-semibold">Hosted MCP access</h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Give agents scoped access to LogLimit through the hosted Cloudflare endpoint.
                  </p>
                </div>
                <Button
                  onClick={() => {
                    setCreatedMcp(null);
                    setMcpName('Agent access');
                    setMcpPreset('observe');
                    setMcpExpiry('90');
                    setShowMcpModal(true);
                  }}
                >
                  Create MCP access
                </Button>
              </div>
            </CardHeader>
            <CardBody>
              <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900 dark:border-blue-800/50 dark:bg-blue-900/20 dark:text-blue-200">
                Observe can read data, Publish can also create and update, and Manage additionally allows confirmed destructive actions. Raw tokens are shown once.
              </div>
              {mcpTokens.length === 0 ? (
                <p className="text-sm text-muted-foreground">No MCP access tokens have been created.</p>
              ) : (
                <div className="space-y-3">
                  {mcpTokens.map((token) => (
                    <div key={token.id} className="flex flex-col gap-3 rounded-lg border p-4 sm:flex-row sm:items-center sm:justify-between">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium">{token.name}</span>
                          <code className="rounded bg-muted px-2 py-0.5 text-xs">{token.token_prefix}…</code>
                          {token.revoked_at && <span className="text-xs font-medium text-red-600">Revoked</span>}
                        </div>
                        <p className="mt-1 text-xs text-muted-foreground">
                          {token.scopes.join(', ')} · Last used {token.last_used_at ? new Date(token.last_used_at * 1000).toLocaleString() : 'never'} · Expires {token.expires_at ? new Date(token.expires_at * 1000).toLocaleDateString() : 'never'}
                        </p>
                      </div>
                      {!token.revoked_at && (
                        <Button variant="destructive" size="sm" onClick={() => handleRevokeMcp(token)}>
                          Revoke
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardBody>
          </Card>
        )}

      </div>

      {/* Create API Key Modal */}
      <Modal
        isOpen={showApiKeyModal}
        onClose={() => setShowApiKeyModal(false)}
        title="Create management API credential"
      >
        {!newApiKey ? (
          <div className="space-y-4">
            <Input
              label="Credential name (optional)"
              type="text"
              value={apiKeyName}
              onChange={(e) => setApiKeyName(e.target.value)}
              placeholder="e.g., CI administration"
            />

            <div className="flex justify-end gap-2 pt-4">
              <Button
                variant="secondary"
                onClick={() => setShowApiKeyModal(false)}
              >
                Cancel
              </Button>
              <Button onClick={handleCreateApiKey}>
                Create Key
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="p-4 bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 border border-green-200 dark:border-green-800/50 rounded-lg">
              <p className="text-sm mb-2">
                <strong>Management credential created.</strong>
              </p>
              <p className="text-xs">
                Copy it now. LogLimit cannot show the secret again. Do not use this as a Node SDK project connection key.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                Credential secret
              </label>
              <div className="flex gap-2">
                <Input
                  type="text"
                  value={newApiKey}
                  readOnly
                  className="font-mono text-sm"
                />
                <Button
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(newApiKey);
                    success('Credential copied to clipboard');
                  }}
                >
                  Copy
                </Button>
              </div>
            </div>

            <div className="flex justify-end">
              <Button
                onClick={() => {
                  setShowApiKeyModal(false);
                  setNewApiKey(null);
                }}
              >
                Done
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        isOpen={showMcpModal}
        onClose={() => {
          setShowMcpModal(false);
          setCreatedMcp(null);
        }}
        title="Hosted MCP access"
        className="max-w-2xl"
      >
        {!createdMcp ? (
          <div className="space-y-4">
            <Input
              label="Access name"
              value={mcpName}
              maxLength={80}
              onChange={(event) => setMcpName(event.target.value)}
              placeholder="e.g. Operations agent"
            />
            <Select
              label="Permission preset"
              value={mcpPreset}
              onChange={(event) => setMcpPreset(event.target.value as 'observe' | 'publish' | 'manage')}
              options={[
                { value: 'observe', label: 'Observe — read only' },
                { value: 'publish', label: 'Publish — read, create, and update' },
                { value: 'manage', label: 'Manage — full access with confirmations' },
              ]}
            />
            <Select
              label="Expiry"
              value={mcpExpiry}
              onChange={(event) => setMcpExpiry(event.target.value)}
              options={[
                { value: '30', label: '30 days' },
                { value: '90', label: '90 days' },
                { value: '365', label: '1 year' },
                { value: 'never', label: 'No expiry' },
              ]}
            />
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="secondary" onClick={() => setShowMcpModal(false)}>Cancel</Button>
              <Button disabled={creatingMcp || !mcpName.trim()} onClick={handleCreateMcp}>
                {creatingMcp ? 'Creating…' : 'Create access'}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-5">
            <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-900 dark:border-green-800/50 dark:bg-green-900/20 dark:text-green-200">
              Copy this token now. LogLimit stores only its SHA-256 hash and cannot show it again.
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">MCP token</label>
              <div className="flex gap-2">
                <Input readOnly value={createdMcp.raw_token} className="font-mono text-xs" />
                <Button onClick={() => copyText(createdMcp.raw_token, 'Token')}>Copy</Button>
              </div>
            </div>
            {([
              ['Codex TOML', createdMcp.configurations.codex_toml],
              ['Claude Code', createdMcp.configurations.claude_code],
              ['Generic JSON', createdMcp.configurations.generic_json],
              ['Smoke command', createdMcp.configurations.smoke_command],
            ] as const).map(([label, value]) => (
              <div key={label}>
                <div className="mb-1 flex items-center justify-between">
                  <label className="text-sm font-medium">{label}</label>
                  <Button variant="outline" size="sm" onClick={() => copyText(value, label)}>Copy</Button>
                </div>
                <pre className="max-h-36 overflow-auto whitespace-pre-wrap break-all rounded-lg bg-muted p-3 text-xs">{value}</pre>
              </div>
            ))}
            <div className="flex justify-end">
              <Button onClick={() => { setShowMcpModal(false); setCreatedMcp(null); }}>Done</Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Revoke Key Modal */}
      <Modal
        isOpen={showRevokeKeyModal}
        onClose={() => setShowRevokeKeyModal(false)}
        title="Revoke management API credential"
      >
        <div className="space-y-4">
          <div className="p-4 bg-red-50 text-red-800 dark:bg-red-900/30 dark:text-red-300 border border-red-200 dark:border-red-800/50 rounded-lg">
            <h4 className="font-medium mb-1">Warning: Destructive Action</h4>
            <p className="text-sm">
              Revoking <strong>{revokeKey?.name}</strong> immediately stops programmatic clients using this credential.
              Browser sessions and Node SDK project connections are unaffected. This action cannot be undone.
            </p>
          </div>

          <p className="text-sm text-muted-foreground">
            Confirm that any automation using this organization-level credential has been updated.
          </p>

          <div className="flex justify-end gap-2 pt-4">
            <Button
              variant="secondary"
              onClick={() => { setShowRevokeKeyModal(false); setRevokeKey(null); }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleRevokeApiKey}
            >
              Revoke Key
            </Button>
          </div>
        </div>
      </Modal>
    </AppShell>
  );
};
